/**
 * roundRobinProjection — pure projector functions translating the wire
 * `RoundRobinState` (proto3 message) into the frontend `RoundRobinStatus`
 * view-model used by Context.
 *
 * The wire message uses `int64` (bigint) for player IDs and a parallel
 * `usernames` slice indexed against `playerOrder`. The view-model uses
 * `number` for IDs and `participantNames` keyed by id. This module is the
 * single point that converts between the two shapes — every other consumer
 * reads the projected `RoundRobinStatus` from Context.
 *
 * Defensive contract: producers MUST emit `len(usernames) == len(playerOrder)`
 * but consumers must not panic when that invariant is violated mid-stream
 * (e.g. participant removed between SSC AddPlayer and partner-service
 * emission). See proto comment on `RoundRobinState.usernames` for the
 * full contract. Mismatched lengths are silently normalized:
 *  - shorter usernames slice → empty string fill ("Player <id>" fallback)
 *  - longer usernames slice → truncated to playerOrder length
 *
 * BigInt -> number conversion uses `Number(bigint)`. proto3 int64 caps at
 * 2^63-1 but the JS view-model uses a 53-bit-precision number; values
 * above MAX_SAFE_INTEGER (2^53-1) silently truncate. This is acceptable
 * for the user IDs and shot counters used today and explicit-by-design
 * (see RoundRobinTurnDisplay's existing String() comparison fallback).
 */
import type {RoundRobinState as WireRoundRobinState} from '../../generated/partner_service_pb';
import type {PlayerState, RotationState} from '../../generated/bay_session_shared_pb';

export type RoundRobinStatus = {
  enabled: boolean, shotsPerPlayer: number, currentPlayerId: number, shotsTakenThisTurn: number, shotsRemaining: number,
  playerOrder: number[], turnComplete: boolean, nextPlayerId: number, participantNames: Record<number, string>
};

/**
 * Project the wire RoundRobinState into the frontend RoundRobinStatus.
 *
 * Returns `null` when:
 *  - the wire message is undefined (no round-robin field present), OR
 *  - `isEnabled === false` (round-robin feature toggled off for this session)
 *
 * Both cases collapse to the same Context state — Context.roundRobinStatus
 * is `null` when round-robin is "not in effect for this player right now",
 * regardless of whether that's because the field is missing or disabled.
 */
export function projectRoundRobinState(wire: WireRoundRobinState | undefined): RoundRobinStatus | null {
  if (!wire || !wire.isEnabled) return null;

  const playerOrder = wire.playerOrder.map(id => Number(id));
  const usernames = wire.usernames ?? [];

  // Build participantNames from parallel slices. Mismatched lengths are
  // normalized: short → "" fill (renderer's `||` fallback produces
  // "Player <id>"); long → truncated by stopping at playerOrder length.
  const participantNames: Record<number, string> = {};
  for (let i = 0; i < playerOrder.length; i++) {
    participantNames[playerOrder[i]] = usernames[i] ?? '';
  }

  return {
    enabled: wire.isEnabled, shotsPerPlayer: wire.shotsPerTurn, currentPlayerId: Number(wire.currentPlayerUserId),
    shotsTakenThisTurn: wire.shotsTaken, shotsRemaining: wire.shotsRemaining, playerOrder,
    turnComplete: wire.turnComplete, nextPlayerId: Number(wire.nextPlayerUserId), participantNames
  };
}

/**
 * SMG-3117: project the bay-session per-user PlayerState's
 * `rotation` + `rotation_participant_names` fields into the
 * frontend `RoundRobinStatus` view-model used by Context. Replaces
 * the legacy `projectRoundRobinState` adapter for the BSA-native
 * mobile flow — the legacy adapter consumed the partner-service-
 * shaped `RoundRobinState` off the kiosk USER_SESSION_UPDATE stream,
 * which under BSA-native mode the mobile no longer subscribes to.
 *
 * Returns `null` when:
 *  - state is undefined / state.rotation is nil (non-ROTATION mode),
 *    OR
 *  - the station has an empty roster.
 *
 * Both collapse to the same "rotation panel not in effect" Context
 * state, mirroring projectRoundRobinState's null contract.
 *
 * The rotation_participant_names map carries first-name strings keyed
 * by user_id (identity-adjacent, non-sensitive — same surface
 * SessionPlayer.first_name exposes for the subject + active player).
 * Missing names default to "" — the renderer's `||` fallback
 * produces "Player <id>" so a server-side roster-loading race
 * doesn't surface a blank chip.
 */
export function projectRotationFromPlayerState(state: PlayerState | undefined): RoundRobinStatus | null {
  const rot: RotationState | undefined = state?.rotation;
  if (!rot) return null;

  const playerOrder = rot.playerOrder.map(id => Number(id));
  if (playerOrder.length === 0) return null;

  const wireNames = state?.rotationParticipantNames ?? {};
  const participantNames: Record<number, string> = {};
  for (const id of playerOrder) {
    // Proto3 map<int64, string> keys serialize as string in the wire
    // → ts-proto map; lookup by stringified id.
    const name = (wireNames as Record<string, string>)[String(id)];
    participantNames[id] = name ?? '';
  }

  return {
    // The server gates rotation population on play_mode === ROTATION;
    // a non-nil rotation field on PlayerState means the station IS
    // in ROTATION. enabled=true mirrors the legacy adapter's
    // isEnabled gate.
    enabled: true, shotsPerPlayer: rot.shotsPerTurn, currentPlayerId: Number(rot.currentPlayerId),
    shotsTakenThisTurn: rot.shotsTaken, shotsRemaining: rot.shotsRemaining, playerOrder, turnComplete: rot.turnComplete,
    nextPlayerId: Number(rot.nextPlayerId), participantNames
  };
}

/**
 * Build the `players` array consumed by `RoundRobinTurnDisplay` from a
 * projected `RoundRobinStatus`. Returns an empty array when status is
 * null so call sites can pass `playersFromStatus(...)` directly to the
 * component without conditional wrapping (the component itself guards
 * against null status).
 *
 * Empty-string usernames are passed through verbatim — the display
 * component renders "Player <id>" via `||` fallback when the username
 * is empty.
 */
export function playersFromStatus(status: RoundRobinStatus | null): { id: number; username: string }[] {
  if (!status) return [];

  return status.playerOrder.map(id => ({id, username: status.participantNames[id] ?? ''}));
}

/**
 * Shallow value-equality between two projected `RoundRobinStatus` objects.
 *
 * The kiosk stream emits a `USER_SESSION_UPDATE` on every session-state
 * change — sub-second cadence under load. The projector allocates a fresh
 * object on every tick, so naive `setRoundRobinStatus(project(...))` calls
 * triggered a Context re-render across every consumer (notably
 * `RoundRobinTurnDisplay`, which re-runs `players.find()` per slot per
 * render). This helper lets the caller skip the setter when the projected
 * value is value-equal to the previous one.
 *
 * Equality rules, in order:
 *  1. Reference equality (same object, including both null) → true
 *  2. One null, one not → false
 *  3. Primitive fields (`enabled`, `shotsPerPlayer`, `currentPlayerId`,
 *     `shotsTakenThisTurn`, `shotsRemaining`, `turnComplete`,
 *     `nextPlayerId`) — strict `===` comparison
 *  4. `playerOrder` — array length-then-element comparison (numbers, so
 *     strict `===` per element)
 *  5. `participantNames` — key-count first, then per-key string `===`
 *
 * NOT a deep-equal: assumes the projector's flat shape. If the projector
 * grows nested fields, this helper must be extended (the projector's
 * type-level shape is the contract this helper relies on).
 */
export function roundRobinStatusEquals(a: RoundRobinStatus | null, b: RoundRobinStatus | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;

  if (a.enabled !== b.enabled || a.shotsPerPlayer !== b.shotsPerPlayer || a.currentPlayerId !== b.currentPlayerId || a.shotsTakenThisTurn !== b.shotsTakenThisTurn || a.shotsRemaining !== b.shotsRemaining || a.turnComplete !== b.turnComplete || a.nextPlayerId !== b.nextPlayerId) {
    return false;
  }

  if (a.playerOrder.length !== b.playerOrder.length) return false;
  for (let i = 0; i < a.playerOrder.length; i++) {
    if (a.playerOrder[i] !== b.playerOrder[i]) return false;
  }

  const aKeys = Object.keys(a.participantNames);
  const bKeys = Object.keys(b.participantNames);
  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (a.participantNames[+key] !== b.participantNames[+key]) return false;
  }

  return true;
}
